import { Form } from "@inertiajs/react"
import { X } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import { useCurrentContext } from "@/hooks/use-current-context"
import { adminActAsAgencyPath } from "@/routes"

/**
 * OKS-683 — App-wide "Acting as {Agency}" banner. The agency-axis twin of
 * `<ActingAsBanner>` (OKS-312).
 *
 * Renders ONLY when `useCurrentContext().actingAsAgency` is set (i.e. an
 * OKS Admin is operating inside the "act as agency" context). Returns
 * `null` for every other request. Mounted in `app-layout.tsx` BESIDE the
 * brand banner, above the role shell, so it persists app-wide.
 *
 * Because the agency and brand act-as contexts are mutually exclusive
 * (each enter action clears the other's session key), at most one of the
 * two banners ever renders.
 *
 * # Layout (mirrors the brand banner exactly)
 *   - Desktop (≥640px): single 40px row — `[badge] Acting as [agency] — exit when done` (left) + Exit (right).
 *   - Mobile (<640px): stacks to 56px — label (top) + Exit (bottom, `min-h-[44px]`).
 *
 * # Colour
 *   - There is no agency-level accent today, so this banner always uses
 *     the amber attention strip (`bg-amber-100 text-amber-900
 *     border-amber-200`) — no colour branch, unlike the brand banner.
 *
 * # Exit
 *   - Inertia `<Form method="delete">` to `adminActAsAgencyPath()`. CSRF
 *     via the Rails meta tag (Crew hard rule — no `<form>` token
 *     plumbing in component code).
 *
 * # Accessibility
 *   - Outer wrap: `role="status"` `aria-live="polite"` so AT announces
 *     the context on render. Colour is not the sole indicator — the
 *     textual "Acting as" label carries the meaning.
 *   - Exit button `aria-label` carries the full "Exit acting as {Agency}".
 *   - Long agency names truncate to 24 chars in the visible label; the
 *     full name is preserved in `aria-label` + the `title` tooltip.
 */
const AGENCY_NAME_MAX_CHARS = 24

export function ActingAsAgencyBanner() {
  const { actingAsAgency } = useCurrentContext()
  const { t } = useTranslation()

  if (!actingAsAgency) {
    return null
  }

  const fullName = actingAsAgency.agency_name
  const truncatedName =
    fullName.length > AGENCY_NAME_MAX_CHARS
      ? `${fullName.slice(0, AGENCY_NAME_MAX_CHARS - 1)}…`
      : fullName

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label={`${t("admin.act_as.banner_agency.label")} ${fullName}`}
      data-testid="acting-as-agency-banner"
      data-agency-id={actingAsAgency.agency_id}
      className="relative z-30 w-full border-b border-amber-200 bg-amber-100 text-amber-900"
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-2 px-4 py-2 sm:h-10 sm:flex-row sm:items-center sm:gap-3 sm:py-0">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <span
            aria-hidden="true"
            data-testid="acting-as-agency-banner-badge"
            className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-amber-900/15 text-xs font-semibold uppercase"
          >
            {actingAsAgency.agency_initial}
          </span>
          <span className="text-sm opacity-80">
            {t("admin.act_as.banner_agency.label")}
          </span>
          <span
            className="truncate text-sm font-semibold"
            title={fullName}
            data-testid="acting-as-agency-banner-agency-name"
          >
            {truncatedName}
          </span>
          <span className="hidden text-xs opacity-70 sm:inline">
            {t("admin.act_as.banner_agency.suffix")}
          </span>
        </div>
        <Form
          action={adminActAsAgencyPath()}
          method="delete"
          className="flex shrink-0 justify-end"
        >
          {({ processing }) => (
            <Button
              type="submit"
              variant="ghost"
              size="sm"
              disabled={processing}
              aria-label={t("admin.act_as.banner_agency.exit_aria", {
                agency: fullName,
              })}
              data-testid="acting-as-agency-banner-exit"
              className="min-h-[44px] gap-1 sm:min-h-0"
            >
              <X aria-hidden="true" className="size-4" />
              {t("admin.act_as.banner_agency.exit")}
            </Button>
          )}
        </Form>
      </div>
    </div>
  )
}
